import React from 'react';
import PropTypes from 'prop-types';
import { gql } from '@common/hooks';
import Filters from 'works/legacy/lib/filters';
import { Table } from 'works/components/table';
import { CandidateLink } from 'works/components/candidate-link';
import { ApplicationStateBadge } from 'works/components/application-state-badge';
import { ApplicationListCtaButton } from './application-list-cta-button';
import { StaffingRequestReviewSubmissionDrawer } from 'works/pages/supplier/staffing-requests/staffing-request/staffing-request-review-submission-drawer';
import {
  CommentInline,
  EmptyContent,
  LoadingState,
  ShowComments,
} from '@common/components';
import { useHasFilters } from 'works/legacy/lib/hooks/use-has-filters';
import {
  STAFFING_REQUEST_APPLICATION_INFO_FRAGMENT,
  OFFER_CONFIRMATION_DRAWER_INFO_FRAGMENT,
} from '@applicant-flow/graphql';

export function ApplicationsList({
  loading,
  staffingRequest,
  data,
  paginationAttrs,
  handleTableChange,
}) {
  const applications = data.records;
  const filters = Filters.getFilters('CandidatesList');
  const hasFilters = useHasFilters(filters);

  // @TODO: remove this logic when CollectionFetcher be decoupled - WORK-6053
  // Checking worksProfile presence to hide incompatible data format while loading
  const hasWorksProfile = applications?.[0]?.worksProfile;

  if (!hasWorksProfile && loading) {
    return <LoadingState />;
  }

  const columns = [
    {
      title: 'Candidate',
      key: 'name',
      dataIndex: 'id',
      width: 380,
      render: (id, record) => {
        return (
          <CandidateLink
            firstName={record.worksProfile.firstName}
            lastName={record.worksProfile.lastName}
            link={`/works/supplier/candidates/${record.worksProfile.id}`}
          />
        );
      },
    },
    {
      title: 'Active Submissions',
      key: 'activeSubmissions',
      render: (record) => {
        return (
          <div className="active-submissions">
            {record.worksProfile.activeSubmissionsCount}
            <style jsx>
              {`
                .active-submissions {
                  padding-left: 50px;
                  font-size: 20px;
                }
              `}
            </style>
          </div>
        );
      },
    },
    {
      title: 'Status',
      key: 'statusInfo',
      render: (record) => {
        return (
          <ApplicationStateBadge
            state={record.state}
            reason={record.rejectionReasonDescription}
            rejectionDetails={record.rejectionDetails}
            timestamp={record.stateChangedAt}
            offerState={record.activeOffer?.state}
            offerTimestamp={record.activeOffer?.stateChangedAt}
            lastComment={
              <ShowComments
                showMode="last"
                commentable={record.activeOffer}
                render={(comment) => (
                  <CommentInline comment={comment} prefix="Offer: " />
                )}
              />
            }
            doNotRehire={record.doNotRehire}
          />
        );
      },
    },
    {
      key: 'buttonInfo',
      render: (record) => {
        return (
          <ApplicationListCtaButton
            staffingRequest={staffingRequest}
            application={record}
          />
        );
      },
    },
  ];

  if (!hasFilters && !loading && applications.length === 0) {
    return (
      <EmptyContent
        imagePath="./supplier/candidates/empty-candidates.png"
        header="Hmm..."
        text="There's nothing here."
      />
    );
  }

  return (
    <Table
      rowKey="id"
      columns={columns}
      dataSource={applications}
      loading={loading}
      pagination={paginationAttrs}
      onChange={handleTableChange}
    />
  );
}

ApplicationsList.fragments = {
  query: gql`
    query SupplierJobPageCollectionQuery(
      $staffingRequestId: ID!
      $limit: Int!
      $offset: Int!
      $orderField: StaffingRequestApplicationOrderField!
      $orderDirection: OrderDirection!
      $states: [StaffingRequestApplicationState!]
    ) {
      paginatedRecords: paginatedStaffingRequestApplications(
        limit: $limit
        offset: $offset
        orderDirection: $orderDirection
        orderField: $orderField
        staffingRequestId: $staffingRequestId
        states: $states
      ) {
        records: staffingRequestApplications {
          id
          state
          rejectionReason
          createdAt
          stateChangedAt
          rejectionReasonDescription
          rejectionDetails
          doNotRehire
          doNotRehireComment
          ...StaffingRequestApplicationInfo
          ...StaffingRequestReviewSubmissionInfo
          activeOffer {
            id
            offerChangeRequests {
              id
              note
              createdAt
              updatedAt
              offerChangeResponse {
                id
                note
                updatedAt
                worksMembership {
                  id
                  name
                }
              }
              worksMembership {
                id
                name
              }
            }
            state
            stateChangedAt
            staffingRequestAssignment {
              id
            }
            ...OfferConfirmationDrawerInfo
          }
          acceptedOffer {
            id
            state
            stateChangedAt
            staffingRequestAssignment {
              id
            }
            ...OfferConfirmationDrawerInfo
          }
          canWithdraw {
            value
          }
          canResubmit {
            value
          }
          worksProfile {
            id
            firstName
            lastName
            fullName
            activeSubmissionsCount
            worksSubmittalPackets {
              id
              archived
              files {
                id
                createdAt
                filename
              }
            }
          }
          staffingRequest {
            id
            externalId
            startDate
            hiringLeader {
              id
              name
            }
            role {
              id
              abbreviation
            }
            organizationBusiness {
              id
              name
            }
            organizationUnit {
              id
              name
            }
          }
          supplier {
            id
            name
            timeZone
          }
        }
        totalCount
      }
    }
    ${STAFFING_REQUEST_APPLICATION_INFO_FRAGMENT}
    ${OFFER_CONFIRMATION_DRAWER_INFO_FRAGMENT}
    ${StaffingRequestReviewSubmissionDrawer.fragments
      .StaffingRequestReviewSubmissionInfo}
  `,
};

ApplicationsList.propTypes = {
  loading: PropTypes.bool,
  fetchMore: PropTypes.func.isRequired,
  staffingRequest: PropTypes.shape({
    id: PropTypes.string.isRequired,
    state: PropTypes.string.isRequired,
  }).isRequired,
  data: PropTypes.shape({
    records: PropTypes.array,
    totalCount: PropTypes.number,
  }).isRequired,
  paginationAttrs: PropTypes.shape({
    limit: PropTypes.number,
    offset: PropTypes.number,
    staffingRequestId: PropTypes.string,
  }),
  handleTableChange: PropTypes.func,
};
